import api, { API } from '@/utils/api.utils';

export interface CareerInquiry {
  id: number;
  user_id: number;
  inquiry_text: string;
  interest_category: string;
  career_title: string;
  career_description: string;
  coursework_description: string;
  career_paths: CareerPath[];
  created_at: string;
  updated_at: string;
}

export interface CareerPath {
  title: string;
  description: string;
  suggested_majors: string[];
  required_skills: string[];
}

export interface CareerAnalysisRequest {
  inquiry: string;
}

export interface CareerAnalysisResponse {
  interest_category: string;
  career_title: string;
  career_description: string;
  coursework_description: string;
  career_paths: CareerPath[];
}

export interface CareerInquiriesListResponse {
  inquiries: CareerInquiry[];
  total_count: number;
}

export interface CareerInquiryCreateRequest {
  inquiry_text: string;
  interest_category: string;
  career_title: string;
  career_description: string;
  coursework_description: string;
  career_paths: CareerPath[];
}

// Interest categories based on the template provided
export const INTEREST_CATEGORIES = [
  'Accountant or actuary',
  'Actor or entertainer',
  'Architect or urban planner',
  'Artist',
  'Business (clerical)',
  'Business executive (management, administrator)',
  'Business owner or proprietor',
  'Business salesperson or buyer',
  'Chef',
  'Clergy (minister, priest)',
  'Clergy (other religious)',
  'Clinical psychologist',
  'College administrator/staff',
  'College teacher',
  'Computer programmer or analyst',
  'Conservationist or forester',
  'Dentist (including orthodontist)',
  'Dietitian or nutritionist',
  'Engineer',
  'Farmer or rancher',
  'Foreign service worker (including diplomat)',
  'Homemaker (full-time)',
  'Hospitality Management (Hotels, Restaurant)',
  'Interior decorator (including designer)',
  'Lab technician or hygienist',
  'Laborer',
  'Law enforcement officer',
  'Lawyer (attorney) or judge',
  'Military service (career)',
  'Musician (performer, composer)',
  'Nurse',
  'Optometrist',
  'Pharmacist',
  'Physician',
  'Policymaker/Government',
  'School counselor',
  'School principal or superintendent',
  'Scientific researcher',
  'Skilled trades',
  'Social, welfare, or recreation worker',
  'Teacher or administrator (elementary)',
  'Teacher or administrator (secondary)',
  'Therapist (physical, occupational, speech)',
  'Veterinarian',
  'Writer or journalist',
  'Other',
  'Undecided'
];

class CareerService {
  private baseURL = `${API.BASE_URL}/api/v1/career`;

  /**
   * Analyze a career inquiry using AI to generate career guidance
   */
  async analyzeCareerInquiry(request: CareerAnalysisRequest): Promise<CareerAnalysisResponse> {
    try {
      const response = await api.post(`${this.baseURL}/analyze`, request);
      return response.data;
    } catch (error) {
      console.error('Error analyzing career inquiry:', error);
      throw error;
    }
  }

  /**
   * Save a career inquiry and analysis result
   */
  async saveCareerInquiry(inquiryData: CareerInquiryCreateRequest): Promise<CareerInquiry> {
    try {
      console.log('🚀 CareerService: Saving career inquiry...', {
        url: this.baseURL,
        dataSize: JSON.stringify(inquiryData).length,
        inquiryText: inquiryData.inquiry_text.substring(0, 50) + '...'
      });
      
      const response = await api.post(this.baseURL, inquiryData);
      
      console.log('✅ CareerService: Save successful', {
        status: response.status,
        dataType: typeof response.data,
        hasId: !!response.data?.id
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ CareerService: Save failed', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data
      });
      throw error;
    }
  }

  /**
   * Get all career inquiries for the current user
   */
  async getUserCareerInquiries(): Promise<CareerInquiriesListResponse> {
    try {
      const response = await api.get(this.baseURL);
      return response.data;
    } catch (error) {
      console.error('Error fetching career inquiries:', error);
      throw error;
    }
  }

  /**
   * Delete a career inquiry
   */
  async deleteCareerInquiry(inquiryId: number): Promise<void> {
    try {
      await api.delete(`${this.baseURL}/${inquiryId}`);
    } catch (error) {
      console.error('Error deleting career inquiry:', error);
      throw error;
    }
  }
}

export default new CareerService();